import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';

const IndividualTeamsLegacyChart = ({ selectedManager, h2hData = [], finalData = [], className = "" }) => {
 const [chartData, setChartData] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [isMobile, setIsMobile] = useState(false);

 // Check for mobile screen size
 useEffect(() => {
   const checkMobile = () => {
     setIsMobile(window.innerWidth < 640);
   };
   
   checkMobile();
   window.addEventListener('resize', checkMobile);
   return () => window.removeEventListener('resize', checkMobile);
 }, []);

 useEffect(() => {
   const calculateIndividualTeamScores = () => {
     try {
       setLoading(true);
       setError(null);

       if (!h2hData.length || !finalData.length) {
         throw new Error('No data available');
       }

       if (!selectedManager) {
         throw new Error('No manager selected');
       }

       // Calculate legacy scores for all individual teams (YearMan combinations)
       const allTeamScores = [];

       // Get all unique years and managers
       const allYears = [...new Set(finalData.map(row => row.Year))].sort();
       const allManagers = [...new Set(finalData.map(row => row.Owner))];

       // Calculate for all manager-year combinations
       allYears.forEach(year => {
         allManagers.forEach(manager => {
           const yearManager = `${year}_${manager}`;
           
           // Get season data
           const seasonRecord = finalData.find(row => row.Year === year && row.Owner === manager);
           if (!seasonRecord) return;

           // Get regular season games for this manager/year
           const seasonGames = h2hData.filter(row => 
             row.Year === year && 
             row.Manager1 === manager && 
             row.Type === 'Regular Season'
           );

           if (!seasonGames.length) return;

           // Calculate regular season record
           const regWins = seasonGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
           const regLosses = seasonGames.reduce((sum, game) => sum + (game.IsLoss || 0), 0);

           // FIXED - Calculate weekly positions exactly like legacyscore.js
           const weeklyPositions = {
             first: 0, second: 0, third: 0, fourth: 0, fifth: 0, sixth: 0,
             seventh: 0, eighth: 0, ninth: 0, tenth: 0, eleventh: 0, twelfth: 0
           };

           seasonGames.forEach(game => {
             const winsThisWeek = game['Wins (This Week)'] || 0;
             
             // Map wins to weekly positions (11 wins = 1st place, 0 wins = 12th place)
             if (winsThisWeek === 11) weeklyPositions.first += 1;        // 1st place
             if (winsThisWeek === 10) weeklyPositions.second += 1;       // 2nd place
             if (winsThisWeek === 9) weeklyPositions.third += 1;        // 3rd place
             if (winsThisWeek === 8) weeklyPositions.fourth += 1;       // 4th place
             if (winsThisWeek === 7) weeklyPositions.fifth += 1;        // 5th place
             if (winsThisWeek === 6) weeklyPositions.sixth += 1;        // 6th place
             if (winsThisWeek === 5) weeklyPositions.seventh += 1;      // 7th place
             if (winsThisWeek === 4) weeklyPositions.eighth += 1;       // 8th place
             if (winsThisWeek === 3) weeklyPositions.ninth += 1;        // 9th place
             if (winsThisWeek === 2) weeklyPositions.tenth += 1;        // 10th place
             if (winsThisWeek === 1) weeklyPositions.eleventh += 1;     // 11th place
             if (winsThisWeek === 0) weeklyPositions.twelfth += 1;      // 12th place
           });

           // Get playoff wins for this season
           const playoffGames = h2hData.filter(game => 
             game.Year === year && 
             game.Manager1 === manager &&
             game.Type === 'Playoffs'
           );
           const playoffWins = playoffGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);

           // Check if this team was scoring champion/loser for the year
           const yearTeams = finalData.filter(row => row.Year === year);
           const maxPF = Math.max(...yearTeams.map(team => team.PF || 0));
           const minPF = Math.min(...yearTeams.map(team => team.PF || 0));
           const scoreChamp = (seasonRecord.PF === maxPF) ? 1 : 0;
           const scoreLoser = (seasonRecord.PF === minPF) ? 1 : 0;

           // Calculate legacy score using EXACT same formula as legacyscore.js
           const legacyScore = 
             // Regular season W/L
             regWins * 5 + regLosses * -5 +
             
             // Weekly scoring (CORRECTED multipliers to match legacyscore.js)
             weeklyPositions.first * 15 + weeklyPositions.twelfth * -15 +      // 1st/12th
             weeklyPositions.second * 5 + weeklyPositions.eleventh * -5 +      // 2nd/11th
             weeklyPositions.third * 4 + weeklyPositions.tenth * -4 +          // 3rd/10th
             weeklyPositions.fourth * 3 + weeklyPositions.ninth * -3 +         // 4th/9th
             weeklyPositions.fifth * 2 + weeklyPositions.eighth * -2 +         // 5th/8th
             weeklyPositions.sixth * 1 + weeklyPositions.seventh * -1 +        // 6th/7th
             
             // Playoff wins
             playoffWins * 10 +
             
             // Standings scoring (CORRECTED multipliers to match legacyscore.js)
             (seasonRecord.Standings === 1 ? 125 : 0) +   // 1st place
             (seasonRecord.Standings === 2 ? 75 : 0) +    // 2nd place
             (seasonRecord.Standings === 3 ? 60 : 0) +    // 3rd place
             (seasonRecord.Standings === 4 ? 40 : 0) +    // 4th place
             (seasonRecord.Standings === 5 ? 30 : 0) +    // 5th place
             (seasonRecord.Standings === 6 ? 15 : 0) +    // 6th place
             (seasonRecord.Standings === 7 ? -15 : 0) +   // 7th place
             (seasonRecord.Standings === 8 ? -30 : 0) +   // 8th place
             (seasonRecord.Standings === 9 ? -40 : 0) +   // 9th place
             (seasonRecord.Standings === 10 ? -50 : 0) +  // 10th place
             (seasonRecord.Standings === 11 ? -60 : 0) +  // 11th place
             (seasonRecord.Standings === 12 ? -80 : 0) +  // 12th place
             
             // Scoring champion/loser
             (scoreChamp * 30) + (scoreLoser * -30);

           allTeamScores.push({
             yearManager: yearManager,
             year: year,
             manager: manager,
             teamName: seasonRecord.Team || `${manager} ${year}`,
             legacyScore: Math.round(legacyScore),
             standings: seasonRecord.Standings,
             record: `${regWins}-${regLosses}`,
             wins: regWins,
             losses: regLosses,
             pointsFor: seasonRecord.PF || 0,
             isSelectedManager: manager === selectedManager
           });
         });
       });

       // Sort by legacy score descending
       allTeamScores.sort((a, b) => b.legacyScore - a.legacyScore);

       // Add overall rank
       allTeamScores.forEach((team, index) => {
         team.overallRank = index + 1;
       });

       console.log('Individual team legacy scores:', allTeamScores);
       setChartData(allTeamScores);
       setLoading(false);

     } catch (err) {
       console.error('Error calculating individual team scores:', err);
       setError(err.message);
       setLoading(false);
     }
   };

   calculateIndividualTeamScores();
 }, [selectedManager, h2hData, finalData]);

 // Mobile-optimized tooltip
 const CustomTooltip = ({ active, payload, label }) => {
   if (!active || !payload || !payload.length) return null;
   
   const data = payload[0].payload;
   const getRankText = (rank) => rank === 1 ? '1st' : rank === 2 ? '2nd' : rank === 3 ? '3rd' : `${rank}th`;
   const percentileRank = ((chartData.length - data.overallRank + 1) / chartData.length * 100).toFixed(1);
   
   return (
     <div className={`bg-gray-900 text-white rounded-lg shadow-lg border border-gray-700 ${
       isMobile ? 'p-2 text-xs' : 'p-3 text-sm'
     }`}>
       <div className="font-semibold mb-2">{data.teamName} ({data.year})</div>
       <div className="space-y-1">
         <div>Legacy Score: <span className="font-medium">{data.legacyScore}</span></div>
         <div>Record: <span className="font-medium">{data.record}</span></div>
         <div>Finish: <span className="font-medium">{getRankText(data.standings)} place</span></div>
         {!isMobile && (
           <>
             <div>Points Scored: <span className="font-medium">{Math.round(data.pointsFor).toLocaleString()}</span></div>
             <div>All-Time Rank: <span className="font-medium">{data.overallRank} of {chartData.length} ({percentileRank}th percentile)</span></div>
           </>
         )}
         {isMobile && (
           <div>Rank: <span className="font-medium">#{data.overallRank} ({percentileRank}%)</span></div>
         )}
       </div>
     </div>
   );
 };

 const getBarColor = (team) => {
   if (team.isSelectedManager) {
     // Selected manager teams - gradient based on score
     if (team.legacyScore >= 150) return '#8b5cf6'; // Purple for great teams
     if (team.legacyScore >= 50) return '#a855f7';  // Lighter purple for good teams
     if (team.legacyScore >= 0) return '#c084fc';   // Even lighter for okay teams
     return '#d8b4fe'; // Very light purple for poor teams
   } else {
     // Other managers - muted colors
     if (team.legacyScore >= 100) return '#e5e7eb';  // Light gray for good teams
     if (team.legacyScore >= 0) return '#f3f4f6';    // Very light gray for okay teams
     return '#f9fafb'; // Almost white for poor teams
   }
 };

 if (loading) {
   return (
     <div className={`bg-white rounded-xl shadow-lg p-3 sm:p-4 lg:p-4 ${className}`}>
       <div className="mb-2">
         <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">
           <span className="hidden sm:inline">{selectedManager}'s Legacy Score by Individual Teams</span>
           <span className="sm:hidden">{selectedManager}'s Team Legacy Scores</span>
         </h3>
         <p className="text-gray-600 text-xs">
           <span className="hidden sm:inline">Single-season legacy scores for all teams (yours highlighted)</span>
           <span className="sm:hidden">Season-by-season legacy scores</span>
         </p>
       </div>
       
       <div className="flex items-center justify-center h-32 sm:h-48">
         <div className="flex flex-col items-center space-y-2">
           <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-gray-200 border-t-purple-600 rounded-full animate-spin"></div>
           <span className="text-gray-600 text-xs font-medium">Calculating scores...</span>
         </div>
       </div>
     </div>
   );
 }

 if (error) {
   return (
     <div className={`bg-white rounded-xl shadow-lg p-3 sm:p-4 lg:p-4 ${className}`}>
       <div className="text-center text-red-500 py-6">
         <div className="text-sm">Error: {error}</div>
       </div>
     </div>
   );
 }

 if (!chartData.length) {
   return (
     <div className={`bg-white rounded-xl shadow-lg p-3 sm:p-4 lg:p-4 ${className}`}>
       <div className="text-center text-gray-500 py-6">
         <div className="text-sm">No team data found for {selectedManager}</div>
       </div>
     </div>
   );
 }

 // Get manager's teams for summary stats
 const managerTeams = chartData.filter(team => team.isSelectedManager);
 const bestTeam = managerTeams[0];
 const worstTeam = managerTeams[managerTeams.length - 1];
 const avgScore = managerTeams.length > 0 ? 
   (managerTeams.reduce((sum, team) => sum + team.legacyScore, 0) / managerTeams.length).toFixed(1) : 0;

 return (
   <div className={`bg-white rounded-xl shadow-lg p-3 sm:p-4 lg:p-4 ${className}`}>
     {/* Title with mobile optimization */}
     <div className="mb-2 sm:mb-3">
       <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">
         <span className="hidden sm:inline">{selectedManager}'s Legacy Score by Individual Teams</span>
         <span className="sm:hidden">{selectedManager}'s Team Legacy Scores</span>
       </h3>
       <p className="text-gray-600 text-xs">
         <span className="hidden sm:inline">Single-season legacy scores for all teams ({selectedManager} highlighted in purple)</span>
         <span className="sm:hidden">Season scores (purple = {selectedManager})</span>
       </p>
     </div>

     {/* Chart with responsive height */}
     <div className={`w-full mb-2 sm:mb-3 ${isMobile ? 'h-48' : 'h-64 sm:h-72'}`}>
       <ResponsiveContainer width="100%" height="100%">
         <BarChart 
           data={chartData}
           margin={{ 
             top: 5, 
             right: isMobile ? 5 : 10, 
             left: isMobile ? 5 : 10, 
             bottom: isMobile ? 15 : 20 
           }}
         >
           <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
           <XAxis 
             dataKey="yearManager"
             tick={false}
             axisLine={{ stroke: '#d1d5db' }}
             tickLine={false}
             height={isMobile ? 15 : 20}
           />
           <YAxis 
             tick={{ fontSize: isMobile ? 8 : 10, fill: '#6b7280' }}
             tickLine={{ stroke: '#d1d5db' }}
             axisLine={{ stroke: '#d1d5db' }}
             width={isMobile ? 25 : 35}
           />
           <Tooltip 
             content={<CustomTooltip />}
           />
           
           {/* Zero reference line */}
           <ReferenceLine 
             y={0} 
             stroke="#000000" 
             strokeDasharray="2 2" 
             opacity={0.6}
             strokeWidth={isMobile ? 1 : 2}
           />
           
           <Bar 
             dataKey="legacyScore" 
             radius={[1, 1, 0, 0]}
             maxBarSize={isMobile ? 8 : 12}
           >
             {chartData.map((team, index) => (
               <Cell key={`cell-${index}`} fill={getBarColor(team)} />
             ))}
           </Bar>
         </BarChart>
       </ResponsiveContainer>
     </div>

     {/* Manager Summary Stats with mobile optimization */}
     {managerTeams.length > 0 && (
       <div className="space-y-2">
         {/* Best and Worst Teams - Responsive layout */}
         <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
           <div className="bg-green-50 border border-green-200 rounded-md p-2">
             <h4 className="text-xs font-semibold text-green-800 mb-1">
               {isMobile ? 'Best' : 'Best Team'}
             </h4>
             <div className="text-sm font-bold text-green-900">
               {isMobile ? `${bestTeam.year}` : `${bestTeam.teamName} (${bestTeam.year})`}
             </div>
             <div className="text-xs text-green-700">
               Score: {bestTeam.legacyScore} • Rank #{bestTeam.overallRank} • {bestTeam.record}
             </div>
           </div>
           
           <div className="bg-red-50 border border-red-200 rounded-md p-2">
             <h4 className="text-xs font-semibold text-red-800 mb-1">
               {isMobile ? 'Worst' : 'Worst Team'}
             </h4>
             <div className="text-sm font-bold text-red-900">
               {isMobile ? `${worstTeam.year}` : `${worstTeam.teamName} (${worstTeam.year})`}
             </div>
             <div className="text-xs text-red-700">
               Score: {worstTeam.legacyScore} • Rank #{worstTeam.overallRank} • {worstTeam.record}
             </div>
           </div>
         </div>

         {/* Overall Stats - Responsive grid */}
         <div className={`grid gap-1 text-center ${
           isMobile ? 'grid-cols-3' : 'grid-cols-5'
         }`}>
           <div className="bg-gray-50 rounded p-1.5">
             <div className="text-sm font-bold text-gray-900">{managerTeams.length}</div>
             <div className="text-xs text-gray-600">Teams</div>
           </div>
           <div className="bg-gray-50 rounded p-1.5">
             <div className="text-sm font-bold text-gray-900">{avgScore}</div>
             <div className="text-xs text-gray-600">Avg</div>
           </div>
           {!isMobile && (
             <>
               <div className="bg-gray-50 rounded p-1.5">
                 <div className="text-sm font-bold text-green-600">
                   {managerTeams.filter(team => team.legacyScore > 0).length}
                 </div>
                 <div className="text-xs text-gray-600">Positive</div>
               </div>
               <div className="bg-gray-50 rounded p-1.5">
                 <div className="text-sm font-bold text-red-600">
                   {managerTeams.filter(team => team.legacyScore < 0).length}
                 </div>
                 <div className="text-xs text-gray-600">Negative</div>
               </div>
             </>
           )}
           <div className="bg-gray-50 rounded p-1.5">
             <div className="text-sm font-bold text-purple-600">
               {managerTeams.filter(team => team.overallRank <= 10).length}
             </div>
             <div className="text-xs text-gray-600">Top 10</div>
           </div>
         </div>

         {/* Mobile-only additional stats */}
         {isMobile && (
           <div className="grid grid-cols-2 gap-1 text-center">
             <div className="bg-gray-50 rounded p-1.5">
               <div className="text-sm font-bold text-green-600">
                 {managerTeams.filter(team => team.legacyScore > 0).length}
               </div>
               <div className="text-xs text-gray-600">Positive</div>
             </div>
             <div className="bg-gray-50 rounded p-1.5">
               <div className="text-sm font-bold text-red-600">
                 {managerTeams.filter(team => team.legacyScore < 0).length}
               </div>
               <div className="text-xs text-gray-600">Negative</div>
             </div>
           </div>
         )}
       </div>
     )}
   </div>
 );
};

export default IndividualTeamsLegacyChart;